import { ChangeDetectionStrategy, Component, computed, inject, model, OnInit, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { form } from "@angular/forms/signals";

import { Building, Check, Copy, Globe, Link, LucideAngularModule, Send, Trash2, Users } from "lucide-angular";

import { DocumentationRoutesConstants } from "@documentation/routes/documentation.routes.constants";
import { DocumentHttpService } from "@documentation/services/document-http.service";
import {
  DocumentShareModel,
  DocumentSharingHttpService,
  SharePermission
} from "@features/documentation/services/document-sharing-http.service";
import { DocumentsStore } from "@features/documentation/state/documents.store";
import { NgbTypeaheadModule, NgbTypeaheadSelectItemEvent } from "@ng-bootstrap/ng-bootstrap";
import { CustomIconComponent } from "@shared/components/custom-icon/custom-icon.component";
import { ModalComponent } from "@shared/components/modal/modal.component";
import { SelectComponent, SelectOption } from "@shared/components/select/select.component";
import { ToggleComponent } from "@shared/components/toggle/toggle.component";
import { UserAvatarComponent } from "@shared/components/user-avatar/user-avatar.component";
import { User, UserRole } from "@shared/models/user.model";

import { BaseShareFormState, BaseShareModalComponent } from "./base-share-modal.component";

interface ShareFormState extends BaseShareFormState {
  permission: SelectOption<string>;
  shareWithOrganization: boolean;
}

@Component({
  selector: "component-share-document-modal",
  imports: [
    ModalComponent,
    LucideAngularModule,
    SelectComponent,
    ToggleComponent,
    UserAvatarComponent,
    NgbTypeaheadModule,
    FormsModule,
    CustomIconComponent
  ],
  templateUrl: "./share-document-modal.component.html",
  styleUrl: "./share-document-modal.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShareDocumentModalComponent extends BaseShareModalComponent<ShareFormState> implements OnInit {
  readonly documentId = model<string>("");

  private readonly _documentSharingHttpService = inject(DocumentSharingHttpService);
  private readonly _documentHttpService = inject(DocumentHttpService);
  private readonly _docsStore = inject(DocumentsStore);

  // Icons
  protected readonly sendIcon = Send;
  protected readonly trashIcon = Trash2;
  protected readonly linkIcon = Link;
  protected readonly copyIcon = Copy;
  protected readonly checkIcon = Check;
  protected readonly globeIcon = Globe;
  protected readonly buildingIcon = Building;
  protected readonly usersIcon = Users;

  protected readonly permissionOptions: SelectOption<string>[] = [
    { text: "Can view", value: "VIEW" },
    { text: "Can edit", value: "EDIT" }
  ];

  // Signals
  protected readonly shares = signal<
    Array<{
      share: DocumentShareModel;
      recipient: (User & { organization?: { id: string; name: string } }) | null;
      organization?: { id: string; name: string } | null;
    }>
  >([]);

  protected readonly overrideOrgShare = signal<boolean | null>(null);

  protected readonly shareLimit = signal<number | null>(null);
  protected readonly canInvite = signal<boolean>(true);
  protected readonly isPublic = signal<boolean>(false);
  protected readonly publicLinkCopied = signal<boolean>(false);

  // Form Signal
  protected readonly formState = signal<ShareFormState>({
    recipient: "",
    selectedUser: null,
    permission: this.permissionOptions[0],
    shareWithOrganization: false
  });

  protected readonly form = form(this.formState);

  protected readonly isOrgAdmin = computed(
    () => this.user()?.role === UserRole.ORGANIZATION_ADMIN || this.user()?.role === UserRole.SUPER_ADMIN
  );

  // Computed
  protected readonly orgShare = computed(() => this.shares().find((s) => !!s.organization));
  protected readonly hasOrgShare = computed(() => {
    const override = this.overrideOrgShare();
    if (override !== null) return override;
    return !!this.orgShare();
  });
  protected readonly userShares = computed(() => this.shares().filter((s) => !s.organization));

  protected readonly isAlreadyShared = computed(() => {
    const selected = this.formState().selectedUser;
    if (!selected) return false;
    return this.shares().some((s) => s.recipient?.id === selected.id);
  });

  ngOnInit(): void {
    const u = this._auth.currentUser();
    if (!u?.hasPlan) {
      this._toast.error("You need to upgrade your plan to share documents");
      this.activeModal.close();
      return;
    }

    const shareLimit = u?.organization?.plan?.maxSharedDocuments;
    if (shareLimit && shareLimit > 0) {
      this.shareLimit.set(shareLimit);
    }

    this.refresh();
    this.checkPublicStatus();
  }

  refresh(): void {
    if (!this.documentId()) return;
    this.loading.set(true);
    this._documentSharingHttpService.listShares(this.documentId()).subscribe({
      next: (rows) => {
        this.shares.set(rows || []);
        const hasAnyForDoc = (rows || []).length > 0;
        this.canInvite.set(hasAnyForDoc || true);
        this.loading.set(false);
        this.overrideOrgShare.set(null); // Clear override when data is fresh
      },
      error: () => this.loading.set(false)
    });
  }

  checkPublicStatus(): void {
    if (!this.documentId()) return;
    this._documentHttpService.getPublicStatus(this.documentId()).subscribe({
      next: (res) => this.isPublic.set(res.isPublic),
      error: () => {}
    });
  }

  onUserSelected(event: NgbTypeaheadSelectItemEvent<User>): void {
    this.handleUserSelected(event, (u) => this.formState.update((s) => ({ ...s, selectedUser: u })));
  }

  updateRecipient(value: string): void {
    this.handleRecipientUpdate(value, this.formState(), (update) => this.formState.update((s) => ({ ...s, ...update })));
  }

  // Template helpers
  updatePermission(option: SelectOption<string>): void {
    this.formState.update((s) => ({ ...s, permission: option }));
  }

  changeOrgPermission(option: SelectOption<string>): void {
    if (this.hasOrgShare()) {
      const share = this.orgShare()?.share;
      if (share) {
        this.changePermission(share, option);
      } else {
        // Optimistic state: update form state so it's used when refresh completes
        this.formState.update((s) => ({ ...s, permission: option }));
      }
    } else {
      this.formState.update((s) => ({ ...s, permission: option }));
    }
  }

  toggleShareWithOrganization(): void {
    const isTurningOn = !this.hasOrgShare();
    // Optimistic update
    this.overrideOrgShare.set(isTurningOn);

    if (!isTurningOn) {
      // Trying to Unshare
      const share = this.orgShare()?.share;
      if (share) {
        this.runOrgShareFlow(
          false,
          {
            title: "Unshare document",
            message:
              "Are you sure you want to unshare this document from your organization? Existing individual shares of this document will not be removed.",
            confirmText: "Unshare"
          },
          () => {
            this._documentSharingHttpService.deleteShare(share.id).subscribe({
              next: () => {
                this._toast.success("Share deleted");
                this.refresh();
                this._docsStore.notifyDocumentUpdated(this.documentId());
              },
              error: () => {
                this._toast.error("Failed to delete share");
                this.refresh();
                this.overrideOrgShare.set(null);
              }
            });
          },
          () => {
            // Cancelled: Revert override with small delay to ensure DOM update
            setTimeout(() => this.overrideOrgShare.set(null), 0);
          }
        );
      } else {
        this.overrideOrgShare.set(null);
      }
    } else {
      // Trying to Share
      const perm = this.formState().permission.value || "VIEW";

      this.runOrgShareFlow(
        true,
        {
          title: "Share with organization",
          message: (count) =>
            `This will make the document available to all ${count - 1} members of your organization and may send them an email notification. Are you sure you want to proceed?`,
          confirmText: "Share"
        },
        () => {
          this.executeOrgShare(perm as SharePermission);
        },
        () => {
          // Cancelled: Revert override with small delay to ensure DOM update
          setTimeout(() => this.overrideOrgShare.set(null), 0);
        }
      );
    }
  }

  private executeOrgShare(permission: SharePermission): void {
    this._documentSharingHttpService.createShare(this.documentId(), null, permission, true).subscribe({
      next: () => {
        this._toast.success("Shared with organization");
        this.refresh();
        this._docsStore.notifyDocumentUpdated(this.documentId());
      },
      error: () => {
        this._toast.error("Failed to share");
        this.refresh();
        this.overrideOrgShare.set(null);
      }
    });
  }

  togglePublic(isPublic: boolean): void {
    this._documentHttpService.setPublic(this.documentId(), isPublic).subscribe({
      next: () => {
        this.isPublic.set(isPublic);
        this._toast.success(isPublic ? "Document is now public" : "Document is now private");
        this._docsStore.notifyDocumentUpdated(this.documentId());
      },
      error: () => {
        this._toast.error("Failed to update visibility");
        // Revert on error
        this.isPublic.update((v) => !v);
      }
    });
  }

  copyLink(): void {
    const url = window.location.origin + DocumentationRoutesConstants.document(this.documentId());
    this.copyLinkToClipboard(url);
  }

  copyPublicLink(): void {
    const url = window.location.origin + DocumentationRoutesConstants.publicDocument(this.documentId());
    navigator.clipboard.writeText(url).then(() => {
      this.publicLinkCopied.set(true);
      setTimeout(() => this.publicLinkCopied.set(false), 3000);
    });
  }

  shareDocument(): void {
    if (!this.documentId()) return;
    const { recipient, selectedUser, permission, shareWithOrganization } = this.formState();

    if (!shareWithOrganization && !selectedUser) {
      return;
    }

    if (this.isAlreadyShared()) {
      this._toast.error("Document already shared with this user");
      return;
    }

    if (!this.canInvite()) {
      this._toast.info(`Share limit reached (${this.shareLimit()})`);
      return;
    }

    this.loading.set(true);

    const recipientValue = selectedUser ? selectedUser.username || selectedUser.email : recipient;

    this._documentSharingHttpService
      .createShare(
        this.documentId(),
        shareWithOrganization ? null : String(recipientValue || "").trim(),
        permission.value as any,
        shareWithOrganization
      )
      .subscribe({
        next: () => {
          this._toast.success("Document shared");
          this._docsStore.notifyDocumentUpdated(this.documentId());
          this.formState.update((s) => ({
            ...s,
            recipient: "",
            selectedUser: null,
            shareWithOrganization: false
          }));
          this.refresh();
        },
        error: (err) => {
          this._toast.error(err.error?.message || "Failed to share document");
          this.loading.set(false);
        }
      });
  }

  changePermission(share: DocumentShareModel, option: SelectOption<string> | null): void {
    if (!option?.value) return;

    const perm = option.value as "VIEW" | "EDIT";
    this._documentSharingHttpService.updateShare(share.id, perm).subscribe({
      next: () => {
        this._toast.success("Permission updated");
        this.refresh();
      },
      error: () => this._toast.error("Failed to update permission")
    });
  }

  deleteShare(share: DocumentShareModel): void {
    const modalRef = this._modalService.confirm(
      "Unshare document",
      "Are you sure you want to unshare this document? This action cannot be undone.",
      "Unshare",
      { closeOtherModals: false }
    );
    modalRef.then((confirmed) => {
      if (confirmed) {
        this._documentSharingHttpService.deleteShare(share.id).subscribe({
          next: () => {
            this._toast.success("Share deleted");
            this.refresh();
            this._docsStore.notifyDocumentUpdated(this.documentId());
          },
          error: () => this._toast.error("Failed to delete share")
        });
      }
    });
  }

  protected getPermissionOption(permissionValue: string): SelectOption<string> | undefined {
    return this.permissionOptions.find((p) => p.value === permissionValue);
  }

  protected isExternalUser(recipient: User & { organization?: { id: string; name: string } }): boolean {
    const myOrgId = this.user()?.organization?.id;
    if (!myOrgId || !recipient?.organization) return false;
    return recipient.organization.id !== myOrgId;
  }

  protected isInherited(recipient: User & { organization?: { id: string; name: string } }): boolean {
    if (!this.hasOrgShare()) return false;
    const myOrgId = this.user()?.organization?.id;
    if (!myOrgId || !recipient?.organization) return false;
    return recipient.organization.id === myOrgId;
  }
}
